import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ExpenseProvider } from './contexts/ExpenseContext'
import { useDarkMode } from './hooks/useDarkMode'

// Pages
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import DashboardPage from './pages/DashboardPage'
import ExpensesPage from './pages/ExpensesPage'
import BudgetsPage from './pages/BudgetsPage'
import BudgetDashboardPage from './pages/BudgetDashboardPage'
import StatsPage from './pages/StatsPage'
import UserProfilePage from './pages/UserProfilePage'
import ArchivesPage from './pages/ArchivesPage'
import AccountsPage from './pages/AccountsPage'
import IncomesPage from './pages/IncomesPage'
import BillsPage from './pages/BillsPage'
import GoalsPage from './pages/GoalsPage'
import DebtsPage from './pages/DebtsPage'
import CollaborationPage from './pages/CollaborationPage'
import NotificationsPage from './pages/NotificationsPage'
import InsightsPage from './pages/InsightsPage'

// Components
import Navbar from './components/Navbar'
import LoadingSpinner from './components/LoadingSpinner'

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth()

  if (loading) {
    return <LoadingSpinner />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return children
}

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth()

  if (loading) {
    return <LoadingSpinner />
  }

  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

// Layout Component
const Layout = ({ children }) => {
  const { isDark, toggleDarkMode } = useDarkMode()
  const { user } = useAuth()
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(() => {
    try {
      const storedSidebarState = localStorage.getItem('expense-tracker-sidebar-expanded')
      if (storedSidebarState === null) {
        return true
      }

      return storedSidebarState === 'true'
    } catch {
      return true
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem('expense-tracker-sidebar-expanded', String(isSidebarExpanded))
    } catch {
      // Ignore storage errors (private mode / disabled storage)
    }
  }, [isSidebarExpanded])

  const contentOffset = user
    ? (isSidebarExpanded ? 'lg:ml-72' : 'lg:ml-20')
    : ''

  const contentTopPadding = user ? 'pt-24' : 'pt-8'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar
        isDark={isDark}
        toggleDarkMode={toggleDarkMode}
        isSidebarExpanded={isSidebarExpanded}
        setIsSidebarExpanded={setIsSidebarExpanded}
      />

      <main className={`px-4 pb-8 transition-all duration-300 ${contentTopPadding} ${contentOffset}`}>
        <div className="mx-auto max-w-7xl">
          {children}
        </div>
      </main>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: isDark ? '#374151' : '#ffffff',
            color: isDark ? '#f3f4f6' : '#111827',
          },
        }}
      />
    </div>
  )
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ExpenseProvider>
          <Layout>
            <Routes>
              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <RegisterPage />
                  </PublicRoute>
                }
              />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/expenses"
                element={
                  <ProtectedRoute>
                    <ExpensesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/budgets"
                element={
                  <ProtectedRoute>
                    <BudgetsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/budget-dashboard"
                element={
                  <ProtectedRoute>
                    <BudgetDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/stats"
                element={
                  <ProtectedRoute>
                    <StatsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <UserProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/archives"
                element={
                  <ProtectedRoute>
                    <ArchivesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts"
                element={
                  <ProtectedRoute>
                    <AccountsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/incomes"
                element={
                  <ProtectedRoute>
                    <IncomesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bills"
                element={
                  <ProtectedRoute>
                    <BillsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/goals"
                element={
                  <ProtectedRoute>
                    <GoalsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/debts"
                element={
                  <ProtectedRoute>
                    <DebtsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/collaboration"
                element={
                  <ProtectedRoute>
                    <CollaborationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/insights"
                element={
                  <ProtectedRoute>
                    <InsightsPage />
                  </ProtectedRoute>
                }
              />

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Layout>
        </ExpenseProvider>
      </AuthProvider>
    </Router>
  )
}

export default App
